// src/components/Navbar.js
// Editorial-quiet navigation: hairline border, unaccented nav links,
// neutral pill CTA. Sits above the Slide Reveal via z-index handling
// in SlideReveal itself (z-[60]).

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createLogger } from '../utils/logger';

const logger = createLogger('Navbar');

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      logger.error('Failed to log out', error);
    }
  };

  // Nav link — inactive is neutral, active adds a hairline underline.
  // No colored background pills, no accent color on hover — the nav
  // should recede so the page content leads.
  const navLinkClass = path => {
    const active = location.pathname === path;
    return `text-sm font-medium transition-colors py-2 border-b ${
      active
        ? 'text-neutral-900 border-neutral-900'
        : 'text-neutral-500 hover:text-neutral-900 border-transparent hover:border-neutral-300'
    }`;
  };

  const mobileLinkClass = path => {
    const active = location.pathname === path;
    return `block px-4 py-3 text-base font-medium border-l-2 ${
      active
        ? 'text-neutral-900 border-neutral-900 bg-neutral-50'
        : 'text-neutral-600 border-transparent hover:text-neutral-900 hover:bg-neutral-50'
    }`;
  };

  return (
    <nav className="fixed w-full z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex-shrink-0 flex items-center"
              aria-label="WWYD home"
            >
              <img
                src="/logo.png"
                alt="WWYD"
                className="h-7 w-auto"
                width="160"
                height="48"
              />
            </Link>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex md:items-center md:space-x-7">
            <Link to="/quiz" className={navLinkClass('/quiz')}>
              Take the Quiz
            </Link>
            <Link to="/about" className={navLinkClass('/about')}>
              About
            </Link>

            {currentUser ? (
              <div className="flex items-center gap-5 pl-3 border-l border-neutral-200">
                <Link to="/results" className={navLinkClass('/results')}>
                  My Results
                </Link>
                <Link
                  to="/profile"
                  className="flex items-center text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors"
                  aria-label="Profile"
                >
                  <div className="w-7 h-7 rounded-full bg-neutral-900 text-white flex items-center justify-center text-[11px] font-semibold">
                    {currentUser.displayName
                      ? currentUser.displayName.charAt(0).toUpperCase()
                      : (currentUser.email?.charAt(0).toUpperCase() ?? 'U')}
                  </div>
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-neutral-400 hover:text-neutral-700 transition-colors text-sm font-medium"
                  aria-label="Log out"
                >
                  Log out
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-5 pl-3 border-l border-neutral-200">
                <Link to="/login" className={navLinkClass('/login')}>
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 text-neutral-600 hover:text-neutral-900"
              aria-expanded={isOpen}
            >
              <span className="sr-only">
                {isOpen ? 'Close main menu' : 'Open main menu'}
              </span>
              {isOpen ? (
                <svg
                  className="block h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.75"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.75"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden ${isOpen ? 'block' : 'hidden'}`}>
        <div className="py-2 bg-white border-t border-neutral-200">
          <Link to="/quiz" className={mobileLinkClass('/quiz')}>
            Take the Quiz
          </Link>
          <Link to="/about" className={mobileLinkClass('/about')}>
            About
          </Link>
          {currentUser ? (
            <>
              <Link to="/results" className={mobileLinkClass('/results')}>
                My Results
              </Link>
              <Link to="/profile" className={mobileLinkClass('/profile')}>
                Profile
              </Link>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-3 text-base font-medium text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 border-l-2 border-transparent"
              >
                Log Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className={mobileLinkClass('/login')}>
                Log In
              </Link>
              <div className="px-4 pt-3">
                <Link
                  to="/signup"
                  className="block text-center bg-neutral-900 hover:bg-neutral-800 text-white text-base font-semibold py-3 rounded-lg transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
